/**
 * Add / edit a journey, including its step list.
 *
 * Steps are edited as a draft array and only committed on save, so reordering
 * and deleting can be abandoned by closing the sheet.
 */
import { useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import {
  CHANNELS, STEP_TYPES, EXITS, PRESETS
} from '../../constants/marketing.js';
import { audienceOf } from '../../domain/marketing.js';
import { salesTeam } from '../../domain/crm.js';
import Sheet, { Group, Field } from '../Sheet.jsx';

const PRESET_KEYS = Object.keys(PRESETS);

/** The editor for one step; which fields appear depends on its type. */
function StepEditor({ step, index, total, templates, team, onChange, onMove, onRemove }) {
  const set = (key, value) => onChange(index, { ...step, [key]: value });

  const applyTemplate = templateId => {
    const t = templates.find(x => x.id === templateId);
    if (!t) { set('tpl', ''); return; }
    onChange(index, { ...step, tpl: templateId, subject: t.subject || '', body: t.body || '', channel: t.channel });
  };

  return (
    <div className="jedit">
      <div className="jeh">
        <span className="jnum">{index + 1}</span>
        <select value={step.type} onChange={e => onChange(index, { type: e.target.value })}>
          {STEP_TYPES.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
        </select>
        <button type="button" className="btn sm" disabled={index === 0} onClick={() => onMove(index, -1)}>↑</button>
        <button type="button" className="btn sm" disabled={index === total - 1} onClick={() => onMove(index, 1)}>↓</button>
        <button type="button" className="btn sm danger" onClick={() => onRemove(index)}><Icon name="trash" /></button>
      </div>

      {step.type === 'wait' ? (
        <div className="num">
          <label>Days to wait</label>
          <input
            type="number" min="0" max="180"
            value={step.days ?? 3}
            onChange={e => set('days', Number(e.target.value))}
          />
        </div>
      ) : step.type === 'message' ? (
        <div className="g2">
          <div className="num">
            <label>Channel</label>
            <select value={step.channel || 'email'} onChange={e => set('channel', e.target.value)}>
              {CHANNELS.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
            </select>
          </div>
          <div className="num">
            <label>Use a template</label>
            <select value={step.tpl || ''} onChange={e => applyTemplate(e.target.value)}>
              <option value="">— written here —</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="num" style={{ gridColumn: '1/-1' }}>
            <label>Subject</label>
            <input value={step.subject || ''} onChange={e => set('subject', e.target.value)} />
          </div>
          <div className="num" style={{ gridColumn: '1/-1' }}>
            <label>Message</label>
            <textarea
              style={{ minHeight: 90 }}
              value={step.body || ''}
              onChange={e => set('body', e.target.value)}
            />
          </div>
        </div>
      ) : step.type === 'task' ? (
        <div className="g2">
          <div className="num">
            <label>Task title</label>
            <input value={step.title || ''} onChange={e => set('title', e.target.value)} />
          </div>
          <div className="num">
            <label>Assign to</label>
            <select value={step.owner || ''} onChange={e => set('owner', e.target.value)}>
              <option value="">the record owner</option>
              {team.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>
      ) : step.type === 'exit' ? (
        <div className="num">
          <label>Leave the journey when</label>
          <select value={step.when || 'paid'} onChange={e => set('when', e.target.value)}>
            {EXITS.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
          </select>
        </div>
      ) : (
        <p className="muted">A desktop notification goes to whoever owns the record.</p>
      )}
    </div>
  );
}

export default function JourneySheet({ id, onClose }) {
  const { S, crm, mkt, mktCtx, saveJourney, runJourneys, toast } = useApp();

  const existing = id ? (mkt.journeys || []).find(j => j.id === id) : null;
  const [form, setForm] = useState(() => existing || {
    name: '', audience: '', enabled: false, recool: 90, exitOnLeave: true
  });
  const [steps, setSteps] = useState(() => structuredClone(existing?.steps || []));

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const changeStep = (index, next) =>
    setSteps(prev => prev.map((s, i) => (i === index ? { ...s, ...next } : s)));

  const moveStep = (index, delta) => setSteps(prev => {
    const out = [...prev];
    const target = index + delta;
    [out[index], out[target]] = [out[target], out[index]];
    return out;
  });

  const removeStep = index => setSteps(prev => prev.filter((_, i) => i !== index));

  const addStep = () =>
    setSteps(prev => [...prev, { type: 'message', channel: 'email', subject: '', body: '' }]);

  const usePreset = key => {
    const preset = PRESETS[key];
    if (!preset) return;
    setSteps(structuredClone(preset.steps));
    if (!form.name) set('name', preset.name);
    toast(`${preset.name} loaded — edit it before running`);
  };

  const save = () => {
    if (!String(form.name).trim()) { toast('Name the journey'); return; }
    if (!form.audience) { toast('Pick who enters it'); return; }
    saveJourney(id, {
      ...form,
      name: String(form.name).trim(),
      recool: Number(form.recool) || 90,
      steps
    });
    onClose();
    runJourneys({ announce: false });
  };

  return (
    <Sheet
      icon="flow"
      tone="g-pink"
      title={id ? 'Edit journey' : 'New journey'}
      onClose={onClose}
      onSave={save}
      saveLabel="Save journey"
    >
      <Group>Journey</Group>

      <Field label="Name" wide>
        <input autoFocus value={form.name || ''} onChange={e => set('name', e.target.value)} />
      </Field>

      <Field label="Who enters it">
        <select value={form.audience || ''} onChange={e => set('audience', e.target.value)}>
          <option value="">— pick an audience —</option>
          {mkt.audiences.map(a => (
            <option key={a.id} value={a.id}>{a.name} ({audienceOf(a, mktCtx).length})</option>
          ))}
        </select>
      </Field>

      <Field label="Running">
        <select value={String(!!form.enabled)} onChange={e => set('enabled', e.target.value === 'true')}>
          <option value="false">Paused</option>
          <option value="true">Running</option>
        </select>
      </Field>

      <Field label="Re-enter after">
        <input
          value={form.recool ?? 90}
          placeholder="days"
          onChange={e => set('recool', e.target.value)}
        />
      </Field>

      <Field label="Leave if they drop out of the audience">
        <select
          value={String(form.exitOnLeave !== false)}
          onChange={e => set('exitOnLeave', e.target.value === 'true')}
        >
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      </Field>

      <Group>Steps</Group>

      <Field label="" wide>
        {steps.map((step, i) => (
          <StepEditor
            key={i}
            step={step}
            index={i}
            total={steps.length}
            templates={mkt.templates}
            team={salesTeam(S, crm)}
            onChange={changeStep}
            onMove={moveStep}
            onRemove={removeStep}
          />
        ))}

        <div className="rowb">
          <button type="button" className="btn" onClick={addStep}><Icon name="plus" />Add a step</button>
          <select
            value=""
            style={{ maxWidth: 220 }}
            onChange={e => { usePreset(e.target.value); e.target.value = ''; }}
          >
            <option value="">Use a proven sequence…</option>
            {PRESET_KEYS.map(k => <option key={k} value={k}>{PRESETS[k].name}</option>)}
          </select>
        </div>
      </Field>
    </Sheet>
  );
}
